/**
 * CanvasEditor — drag-to-nudge layout editor for Canvas-mode HTML.
 *
 * Why this exists:
 *   Canvas mode hands the LLM a wide brush. Even with brand CSS + a tight
 *   system prompt, the model occasionally ships a card row that's slightly
 *   misaligned, a footer that bleeds 8pt past the page edge, or a hero
 *   number that's begging to slide 30pt right. Rather than iterate prompts
 *   forever, this editor lets the rep grab any top-level child of a
 *   `<div class="page">` and drag it to taste. The modified HTML is then
 *   re-rendered to PDF server-side (WeasyPrint only — no second LLM call).
 *
 * How it works:
 *   1. Receive an HTML document (brand CSS already inlined by the server).
 *   2. Inject a small `<script>` near the end of `<body>` that decorates
 *      every direct child of `.page` with drag handles. The script also
 *      listens for a postMessage from the parent asking for the
 *      serialized DOM.
 *   3. Drop the augmented HTML into a sandboxed iframe via `srcDoc`.
 *   4. Browser at 96dpi renders 1pt = 1.333px. The drag handler converts
 *      pixel deltas → point deltas (×0.75) so what the rep sees in-browser
 *      matches WeasyPrint's PDF render exactly.
 *   5. Movement is expressed as inline `position: relative; left: Xpt;
 *      top: Ypt`. Pure CSS that WeasyPrint reliably reproduces — and
 *      crucially NOT `transform:` (banned by the canvas system prompt
 *      because WeasyPrint <63 choked on it; the fix is in but we keep
 *      the rule).
 *   6. On Done: postMessage to the iframe asking for the serialized
 *      document; on receive, hand the HTML to the parent via onSave.
 */
import { useEffect, useRef, useState } from 'react'
import { colors } from '../theme'

// 96dpi browser → 72dpi WeasyPrint. 1 CSS pixel = 0.75 points.
// This ratio is duplicated in the injected DRAG_SCRIPT string below — they
// must stay in sync. Kept here as a reference / docstring even though the
// runtime uses the in-script copy.
// const PX_TO_PT = 72 / 96

interface Props {
  /** The HTML document to edit. Brand CSS should already be injected. */
  html: string
  /** Called with the modified HTML when the rep clicks Save. */
  onSave: (modifiedHtml: string) => void
  /** Called when the rep dismisses without saving. */
  onCancel: () => void
}

/**
 * JS that gets injected into the iframe to make `.page > *` draggable.
 * Kept as a string template (no JSX) so we can inject it via srcDoc.
 *
 * IMPORTANT: WeasyPrint ignores <script> blocks at render time, so this
 * script is purely a browser-side enhancement. Saving the document
 * preserves the script tag but it has no effect on the PDF output.
 */
const DRAG_SCRIPT = `
(function () {
  var PX_TO_PT = 72 / 96;

  // Match the parent's PostMessage origin so we don't get spammed by
  // anything else. The iframe runs srcDoc → origin is "null" — we accept
  // any "*" message because the iframe is sandboxed to its own srcDoc.
  function postParent(msg) {
    try { window.parent.postMessage(msg, '*'); } catch (_) {}
  }

  // Read current inline left/top (in pt) — defaults to 0 if unset.
  function readOffset(el) {
    var left = parseFloat(el.style.left) || 0;
    var top  = parseFloat(el.style.top)  || 0;
    return { left: left, top: top };
  }

  function writeOffset(el, leftPt, topPt) {
    el.style.position = 'relative';
    el.style.left = leftPt.toFixed(2) + 'pt';
    el.style.top  = topPt.toFixed(2)  + 'pt';
  }

  var selected = null;
  function selectElement(el) {
    if (selected === el) return;
    if (selected) selected.classList.remove('__parkify-selected');
    selected = el;
    if (el) el.classList.add('__parkify-selected');
  }

  function decorate(el) {
    if (el.dataset.parkifyDraggable === '1') return;
    el.dataset.parkifyDraggable = '1';
    el.classList.add('__parkify-draggable');
    // Don't override author position math unless they didn't set anything —
    // setting position:relative without left/top is layout-neutral.
    var cs = window.getComputedStyle(el);
    if (cs.position === 'static') el.style.position = 'relative';

    el.addEventListener('mousedown', function (e) {
      // Ignore clicks on text inputs / contenteditable / links inside the box
      var tag = (e.target && e.target.tagName) || '';
      if (tag === 'A' || tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      e.stopPropagation();
      selectElement(el);

      var startX = e.clientX;
      var startY = e.clientY;
      var offset = readOffset(el);
      var startLeft = offset.left;
      var startTop  = offset.top;

      function onMove(ev) {
        var dxPx = ev.clientX - startX;
        var dyPx = ev.clientY - startY;
        writeOffset(el, startLeft + dxPx * PX_TO_PT, startTop + dyPx * PX_TO_PT);
        postParent({ kind: 'parkify-canvas-edit', leftPt: parseFloat(el.style.left), topPt: parseFloat(el.style.top) });
      }
      function onUp() {
        document.removeEventListener('mousemove', onMove, true);
        document.removeEventListener('mouseup', onUp, true);
      }
      document.addEventListener('mousemove', onMove, true);
      document.addEventListener('mouseup', onUp, true);
    }, false);
  }

  function decorateAll() {
    var pages = document.querySelectorAll('.page');
    pages.forEach(function (page) {
      Array.prototype.forEach.call(page.children, function (child) {
        if (child.nodeType !== 1) return;
        decorate(child);
      });
    });
  }

  // Keyboard nudge — arrow keys move the selected element 2pt at a time
  // (Shift = 10pt). Lets the rep get pixel-perfect without mouse jitter.
  function onKey(e) {
    if (!selected) return;
    var step = e.shiftKey ? 10 : 2;
    var off = readOffset(selected);
    if (e.key === 'ArrowLeft')  { writeOffset(selected, off.left - step, off.top); e.preventDefault(); }
    if (e.key === 'ArrowRight') { writeOffset(selected, off.left + step, off.top); e.preventDefault(); }
    if (e.key === 'ArrowUp')    { writeOffset(selected, off.left, off.top - step); e.preventDefault(); }
    if (e.key === 'ArrowDown')  { writeOffset(selected, off.left, off.top + step); e.preventDefault(); }
    if (e.key === 'Escape')     { selectElement(null); }
    if (e.key === 'r' || e.key === 'R') {
      if (selected.style.left || selected.style.top) {
        selected.style.left = '';
        selected.style.top = '';
      }
    }
  }
  document.addEventListener('keydown', onKey, false);

  // Click outside of a draggable → deselect.
  document.addEventListener('mousedown', function (e) {
    var t = e.target;
    while (t && t !== document.body) {
      if (t.dataset && t.dataset.parkifyDraggable === '1') return;
      t = t.parentNode;
    }
    selectElement(null);
  }, false);

  // Parent asks for the serialized DOM (Save / Done click) — strip the
  // editor styles + draggable markers we added, then post the cleaned
  // HTML back. We DO keep this script in the output because WeasyPrint
  // ignores <script>; the markers + classes are harmless but cleaner
  // to remove.
  function onMessage(ev) {
    if (!ev.data || ev.data.kind !== 'parkify-canvas-serialize') return;
    // Clone the document so we don't mutate the live view.
    var clone = document.documentElement.cloneNode(true);
    var draggables = clone.querySelectorAll('[data-parkify-draggable]');
    draggables.forEach(function (el) {
      el.removeAttribute('data-parkify-draggable');
      el.classList.remove('__parkify-draggable');
      el.classList.remove('__parkify-selected');
    });
    // Drop the editor-only stylesheet + this script.
    var editorAssets = clone.querySelectorAll('[data-parkify-editor]');
    editorAssets.forEach(function (n) { n.parentNode.removeChild(n); });
    var html = '<!doctype html>\\n' + clone.outerHTML;
    postParent({ kind: 'parkify-canvas-serialized', html: html });
  }
  window.addEventListener('message', onMessage, false);

  // Initial decorate after DOM ready (if not already).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', decorateAll, false);
  } else {
    decorateAll();
  }

  // Re-decorate periodically in case the document mutates (defensive).
  setTimeout(decorateAll, 200);

  // Signal ready.
  postParent({ kind: 'parkify-canvas-ready' });
})();
`

// Editor-only CSS — outline + grab cursor on hover, solid outline + bring
// to front when selected. Lives inside the iframe; not part of the saved
// document.
const EDITOR_STYLE = `
.__parkify-draggable {
  cursor: grab !important;
  outline: 1pt dashed rgba(127, 63, 152, 0.0);
  outline-offset: 2pt;
  transition: outline-color 80ms ease;
}
.__parkify-draggable:hover {
  outline-color: rgba(127, 63, 152, 0.55) !important;
}
.__parkify-draggable.__parkify-selected {
  outline: 1.5pt solid #7F3F98 !important;
  outline-offset: 4pt;
  z-index: 999 !important;
  cursor: grabbing !important;
}
.__parkify-draggable:active {
  cursor: grabbing !important;
}
`

/** Inject the drag script + editor styles into the HTML document. The
 *  serializer in the iframe later strips both back out before save. */
function augmentHtmlForEditing(html: string): string {
  const styleTag = `<style data-parkify-editor="1">${EDITOR_STYLE}</style>`
  const scriptTag = `<script data-parkify-editor="1">${DRAG_SCRIPT}</script>`
  let out = html
  // Style: tuck it after the brand <style> block (any <style>) — these
  // are scoped by class so order doesn't matter much, but keeping them
  // late means the `!important` rules definitely win.
  if (out.includes('</head>')) {
    out = out.replace('</head>', styleTag + '</head>')
  } else {
    out = styleTag + out
  }
  // Script: end of body so it runs after DOM is parsed.
  if (out.includes('</body>')) {
    out = out.replace('</body>', scriptTag + '</body>')
  } else {
    out = out + scriptTag
  }
  return out
}

export default function CanvasEditor({ html, onSave, onCancel }: Props) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const [ready, setReady] = useState(false)
  const [saving, setSaving] = useState(false)
  const [zoom, setZoom] = useState(0.85)
  // Stash the most recent serialized HTML the iframe sent back so we have
  // it ready when the rep clicks Save (we don't need a round-trip every
  // single drag, just on save).
  const pendingResolveRef = useRef<((html: string) => void) | null>(null)

  const editorHtml = (() => augmentHtmlForEditing(html))()

  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      const data = ev.data as { kind?: string; html?: string } | null
      if (!data || !data.kind) return
      if (data.kind === 'parkify-canvas-ready') {
        setReady(true)
        return
      }
      if (data.kind === 'parkify-canvas-serialized') {
        const cb = pendingResolveRef.current
        pendingResolveRef.current = null
        if (cb && typeof data.html === 'string') cb(data.html)
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  function requestSerializedHtml(): Promise<string> {
    return new Promise((resolve, reject) => {
      const iframe = iframeRef.current
      if (!iframe || !iframe.contentWindow) {
        reject(new Error('Editor iframe not ready'))
        return
      }
      pendingResolveRef.current = resolve
      iframe.contentWindow.postMessage({ kind: 'parkify-canvas-serialize' }, '*')
      // Safety: if the iframe never responds (script error?), reject after
      // 4s so the UI doesn't hang.
      setTimeout(() => {
        if (pendingResolveRef.current === resolve) {
          pendingResolveRef.current = null
          reject(new Error('Editor did not respond — try regenerating canvas'))
        }
      }, 4000)
    })
  }

  async function handleSave() {
    setSaving(true)
    try {
      const modified = await requestSerializedHtml()
      onSave(modified)
    } catch (e) {
      // Bubble back to the rep — caller shows in its own error pane.
      console.error('canvas-editor: save failed', e)
      setSaving(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(35,31,32,0.65)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
      }}
    >
      <div
        style={{
          flex: 1,
          margin: 24,
          background: colors.bg,
          borderRadius: 16,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            padding: '16px 24px',
            background: colors.panel,
            borderBottom: `1px solid ${colors.divider}`,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                fontSize: 10,
                letterSpacing: '2.5px',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: colors.purple,
              }}
            >
              Edit Layout
            </span>
            <span style={{ fontSize: 12, color: colors.textMuted }}>
              Click any box → drag, or arrow keys (Shift = 10pt). R resets.
            </span>
          </div>

          <div style={{ flex: 1 }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, color: colors.textMuted, fontWeight: 600 }}>
              ZOOM
            </span>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(0.4, Number((z - 0.1).toFixed(2))))}
              style={zoomBtnStyle}
              aria-label="Zoom out"
            >
              −
            </button>
            <span style={{ fontSize: 12, color: colors.text, minWidth: 40, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(1.5, Number((z + 0.1).toFixed(2))))}
              style={zoomBtnStyle}
              aria-label="Zoom in"
            >
              +
            </button>
          </div>

          <button
            type="button"
            onClick={onCancel}
            style={{
              background: 'transparent',
              color: colors.text,
              border: `1px solid ${colors.divider}`,
              borderRadius: 8,
              padding: '9px 18px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!ready || saving}
            style={{
              background: colors.purple,
              color: colors.white,
              border: 'none',
              borderRadius: 8,
              padding: '10px 22px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              cursor: ready && !saving ? 'pointer' : 'not-allowed',
              opacity: ready && !saving ? 1 : 0.6,
            }}
          >
            {saving ? 'Rendering…' : 'Apply & Re-render PDF'}
          </button>
        </div>

        {/* Editor viewport — iframe with srcDoc + drag script */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            padding: 24,
            background: colors.bg,
          }}
        >
          <div
            style={{
              // Page is 720pt × 540pt = 960px × 720px at 96dpi. Each .page
              // is `page-break-after: always` in the brand CSS so multiple
              // pages stack vertically — but we also want the iframe to be
              // tall enough to scroll. Set min-height generously and let it
              // grow.
              width: 960 * zoom,
              flexShrink: 0,
            }}
          >
            <iframe
              ref={iframeRef}
              srcDoc={editorHtml}
              title="Canvas editor"
              sandbox="allow-scripts allow-same-origin"
              style={{
                width: 960,
                height: 720 * 10, // up to 10 pages tall — iframe scrolls within itself
                border: 'none',
                background: colors.white,
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(35,31,32,0.12)',
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const zoomBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  border: `1px solid ${colors.divider}`,
  background: colors.white,
  fontSize: 16,
  fontWeight: 700,
  color: colors.text,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
}
